/**
 * Travel Stats Dashboard Component
 * 
 * Main component displaying user travel statistics
 */

import React, { useState, useEffect } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  RefreshControl,
  ActivityIndicator,
  Pressable,
} from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { Screen } from '@/components/layout/Screen';
import { Heading, Body, Caption } from '@/components/ui/Text';
import { StatCard } from './StatCard';
import { Card } from '@/components/ui/Card';
import { ErrorState } from '@/components/ui/ErrorState';
import { useTheme } from '@/theme';
import { useUserStore } from '@/state/userStore';
import { getUserStats, updateUserStatsFromTrips } from '@/services/userStats';
import type { UserStats } from '@/services/userStats';
import { fetchUserTrips } from '@/services/trips';
import { getYearlyStats } from '@/services/gamification';

export function TravelStatsDashboard() {
  const theme = useTheme();
  const router = useRouter();
  const handleClose = () => {
    if (typeof router.canGoBack === 'function' && router.canGoBack()) {
      router.back();
      return;
    }
    router.replace('/(tabs)/profile');
  };

  const firebaseUser = useUserStore((state) => state.firebaseUser);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [yearlyStats, setYearlyStats] = useState<{
    countries: number;
    cities: number;
    tripDays: number;
    trips: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [statsError, setStatsError] = useState<string | null>(null);

  const currentYear = new Date().getFullYear();

  useEffect(() => {
    loadStats();
  }, [firebaseUser?.uid]);

  const loadStats = async () => {
    if (!firebaseUser?.uid) return;

    try {
      setStatsError(null);
      setLoading(true);
      const { trips } = await fetchUserTrips(firebaseUser.uid);
      const yearStats = getYearlyStats(trips, currentYear);

      let resolvedStats: UserStats | null = null;
      if (trips.length > 0) {
        try {
          resolvedStats = await updateUserStatsFromTrips(firebaseUser.uid, trips);
        } catch (syncError) {
          console.warn('[TravelStatsDashboard] Sync userStats from trips failed:', syncError);
          resolvedStats = await getUserStats(firebaseUser.uid);
        }
      } else {
        resolvedStats = await getUserStats(firebaseUser.uid);
      }

      setStats(resolvedStats);
      setYearlyStats(yearStats);
    } catch (error) {
      console.error('[TravelStatsDashboard] Error loading stats:', error);
      setStatsError("Couldn't load travel stats. Check your connection and try again.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadStats();
  };

  const closeHeaderAction = (
    <Pressable
      onPress={handleClose}
      style={({ pressed }) => ({ opacity: pressed ? 0.7 : 1 })}
      accessibilityLabel="Close"
      accessibilityRole="button"
    >
      <Ionicons name="close" size={24} color={theme.colors.text} />
    </Pressable>
  );

  if (loading && !stats) {
    return (
      <Screen
        title="Travel Statistics"
        headerAction={closeHeaderAction}
        scrollable={false}
      >
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.colors.accent} />
        </View>
      </Screen>
    );
  }

  if (statsError && !stats) {
    return (
      <Screen
        title="Travel Statistics"
        headerAction={closeHeaderAction}
        scrollable={false}
      >
        <ErrorState
          variant="full"
          title="Couldn't load travel stats"
          message={statsError}
          onRetry={loadStats}
          retryLabel="Retry"
        />
      </Screen>
    );
  }

  const countriesCount = stats?.countriesVisited.length || 0;
  const citiesCount = stats?.citiesVisited.length || 0;
  const landmarksCount = stats?.landmarksVisited.length || 0;
  const totalDays = stats?.totalTripDays || 0;
  const continentsCount = stats?.continentsVisited.length || 0;
  const streak = stats?.currentStreak || 0;

  // Determine hero stat (most impressive metric)
  const heroStat = countriesCount >= citiesCount ? 'countries' : 'cities';
  const heroValue = heroStat === 'countries' ? countriesCount : citiesCount;
  const heroIcon = heroStat === 'countries' ? 'globe-outline' : 'business-outline';
  const heroColor = heroStat === 'countries' ? theme.colors.primary : theme.colors.accent;
  const heroYearly = heroStat === 'countries' ? yearlyStats?.countries : yearlyStats?.cities;

  // Calculate percentage change (simplified - would need previous year data for real calculation)
  const yearlyPercentage = heroYearly && heroValue > 0
    ? Math.round((heroYearly / heroValue) * 100)
    : undefined;

  return (
    <Screen
      title="Travel Statistics"
      subtitle="Your journey around the world"
      headerAction={closeHeaderAction}
      scrollable={false}
    >
      <ScrollView
        style={styles.container}
        contentContainerStyle={styles.content}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        {statsError ? (
          <View style={{ marginBottom: 16 }}>
            <ErrorState
              title="Couldn't refresh travel stats"
              message={statsError}
              onRetry={loadStats}
              retryLabel="Retry"
            />
          </View>
        ) : null}

        <Pressable
          onPress={() => router.push('/gamification/travel-map')}
          style={({ pressed }) => [
            styles.mapLinkRow,
            {
              opacity: pressed ? 0.88 : 1,
              backgroundColor: theme.colors.surface,
              borderColor: theme.colors.borderMuted,
            },
          ]}
        >
          <Ionicons name="map-outline" size={22} color={theme.colors.accent} />
          <Body style={[styles.mapLinkText, { color: theme.colors.text }]}>Countries map</Body>
          <Ionicons name="chevron-forward" size={20} color={theme.colors.textTertiary} />
        </Pressable>

        {/* Hero Stat - Most impressive metric */}
        {heroValue > 0 && (
          <StatCard
            hero
            title={heroStat === 'countries' ? 'Countries Visited' : 'Cities Explored'}
            value={heroValue}
            subtitle={`${heroYearly || 0} this year`}
            icon={heroIcon}
            iconColor={heroColor}
            trend={
              heroYearly && heroYearly > 0
                ? {
                    value: heroYearly,
                    label: 'this year',
                    percentage: yearlyPercentage,
                  }
                : undefined
            }
            progress={
              heroYearly && heroYearly > 0
                ? {
                    current: heroYearly,
                    target: Math.max(heroYearly * 2, 10), // Dynamic target
                    showRing: true,
                  }
                : undefined
            }
          />
        )}

        {/* Main Stats Grid */}
        <View style={styles.statsGrid}>
          {heroStat !== 'countries' && (
            <StatCard
              title="Countries"
              value={countriesCount}
              subtitle={`${yearlyStats?.countries || 0} this year`}
              icon="globe-outline"
              iconColor={theme.colors.primary}
              trend={
                yearlyStats && yearlyStats.countries > 0
                  ? {
                      value: yearlyStats.countries,
                      label: 'this year',
                      percentage: countriesCount > 0
                        ? Math.round((yearlyStats.countries / countriesCount) * 100)
                        : undefined,
                    }
                  : undefined
              }
            />
          )}
          {heroStat !== 'cities' && (
            <StatCard
              title="Cities"
              value={citiesCount}
              subtitle={`${yearlyStats?.cities || 0} this year`}
              icon="business-outline"
              iconColor={theme.colors.accent}
              trend={
                yearlyStats && yearlyStats.cities > 0
                  ? {
                      value: yearlyStats.cities,
                      label: 'this year',
                      percentage: citiesCount > 0
                        ? Math.round((yearlyStats.cities / citiesCount) * 100)
                        : undefined,
                    }
                  : undefined
              }
            />
          )}
        </View>

        <View style={styles.statsGrid}>
          <StatCard
            title="Landmarks"
            value={landmarksCount}
            subtitle="Places visited"
            icon="location-outline"
            iconColor={theme.colors.success}
          />
          <StatCard
            title="Trip days (all-time)"
            value={totalDays}
            subtitle={`${yearlyStats?.tripDays || 0} days this year`}
            icon="calendar-outline"
            iconColor={theme.colors.warning}
            trend={
              yearlyStats && yearlyStats.tripDays > 0
                ? {
                    value: yearlyStats.tripDays,
                    label: 'this year',
                  }
                : undefined
            }
          />
        </View>

        {/* Additional Stats */}
        <Card variant="elevated" elevation={1} padding={16} style={styles.additionalStats}>
          <View style={styles.additionalStatsRow}>
            <View style={styles.additionalStatItem}>
              <Body style={[styles.additionalStatLabel, { color: theme.colors.textSecondary }]}>
                Continents
              </Body>
              <Heading style={styles.additionalStatValue}>{continentsCount}</Heading>
            </View>
            <View style={[styles.divider, { backgroundColor: theme.colors.borderMuted }]} />
            <View style={styles.additionalStatItem}>
              <Body style={[styles.additionalStatLabel, { color: theme.colors.textSecondary }]}>
                Travel Streak
              </Body>
              <Heading style={styles.additionalStatValue}>
                {streak} {streak === 1 ? 'month' : 'months'}
              </Heading>
            </View>
            <View style={[styles.divider, { backgroundColor: theme.colors.borderMuted }]} />
            <View style={styles.additionalStatItem}>
              <Body style={[styles.additionalStatLabel, { color: theme.colors.textSecondary }]}>
                Longest Streak
              </Body>
              <Heading style={styles.additionalStatValue}>
                {stats?.longestStreak || 0} {stats?.longestStreak === 1 ? 'month' : 'months'}
              </Heading>
            </View>
          </View>
        </Card>

        {/* Yearly Summary Card */}
        {yearlyStats && yearlyStats.trips > 0 && (
          <Card variant="elevated" elevation={1} padding={16} style={styles.yearlyCard}>
            <Heading style={styles.yearlyTitle}>Your {currentYear} in Travel</Heading>
            <View style={styles.yearlyStats}>
              <View style={styles.yearlyStatItem}>
                <Body style={{ color: theme.colors.textSecondary }}>Trips</Body>
                <Heading style={styles.yearlyStatValue}>{yearlyStats.trips}</Heading>
              </View>
              <View style={styles.yearlyStatItem}>
                <Body style={{ color: theme.colors.textSecondary }}>Countries</Body>
                <Heading style={styles.yearlyStatValue}>{yearlyStats.countries}</Heading>
              </View>
              <View style={styles.yearlyStatItem}>
                <Body style={{ color: theme.colors.textSecondary }}>Cities</Body>
                <Heading style={styles.yearlyStatValue}>{yearlyStats.cities}</Heading>
              </View>
              <View style={styles.yearlyStatItem}>
                <Body style={{ color: theme.colors.textSecondary }}>Days</Body>
                <Heading style={styles.yearlyStatValue}>{yearlyStats.tripDays}</Heading>
              </View>
            </View>
          </Card>
        )}

        {/* Countries List */}
        {stats && stats.countriesVisited.length > 0 && (
          <Card variant="elevated" elevation={1} padding={16} style={styles.countriesCard}>
            <Heading style={styles.sectionTitle}>Countries Visited</Heading>
            <View style={styles.countriesList}>
              {stats.countriesVisited.slice(0, 10).map((country, index) => (
                <View key={country.country} style={styles.countryItem}>
                  <Body style={styles.countryName}>{country.country}</Body>
                  <Caption style={{ color: theme.colors.textTertiary }}>
                    {country.visitCount} {country.visitCount === 1 ? 'visit' : 'visits'}
                  </Caption>
                </View>
              ))}
              {stats.countriesVisited.length > 10 && (
                <Body style={{ color: theme.colors.textSecondary, marginTop: 8 }}>
                  +{stats.countriesVisited.length - 10} more countries
                </Body>
              )}
            </View>
          </Card>
        )}

        {!stats && (!yearlyStats || yearlyStats.trips === 0) && (
          <Card variant="elevated" elevation={1} padding={24} style={styles.emptyState}>
            <Body style={{ color: theme.colors.textSecondary, textAlign: 'center' }}>
              Complete your first trip to start tracking your travel statistics!
            </Body>
          </Card>
        )}
      </ScrollView>
    </Screen>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    padding: 16,
  },
  mapLinkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 16,
    gap: 10,
  },
  mapLinkText: {
    flex: 1,
    fontWeight: '600',
    fontSize: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  statsGrid: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 12,
  },
  additionalStats: {
    marginTop: 12,
    marginBottom: 12,
  },
  additionalStatsRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
  },
  additionalStatItem: {
    flex: 1,
    alignItems: 'center',
  },
  additionalStatLabel: {
    fontSize: 12,
    marginBottom: 4,
  },
  additionalStatValue: {
    fontSize: 20,
    fontWeight: '700',
  },
  divider: {
    width: 1,
    height: 40,
  },
  yearlyCard: {
    marginTop: 12,
    marginBottom: 12,
  },
  yearlyTitle: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 16,
  },
  yearlyStats: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  yearlyStatItem: {
    alignItems: 'center',
  },
  yearlyStatValue: {
    fontSize: 24,
    fontWeight: '700',
    marginTop: 4,
  },
  countriesCard: {
    marginTop: 12,
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 16,
  },
  countriesList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  countryItem: {
    minWidth: '30%',
    marginBottom: 8,
  },
  countryName: {
    fontSize: 14,
    fontWeight: '500',
    marginBottom: 2,
  },
  emptyState: {
    marginTop: 24,
  },
});

